import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CierreCajaService, FiltrosCierre } from 'src/app/services/integracion-pos/cierre-caja.service';

/**
 * «Cierre de caja» — consola de CrediTotal.
 *
 * 🔴 PEDIDO DE JUAN, 2026-09-20: *«los administradores también pueden bajarse un
 * cierre de caja donde le traiga todos los créditos, cuándo fue la inicial y
 * cuánto fue, y cuánto se financió»*.
 *
 * ── POR QUÉ ES UNA PANTALLA PROPIA Y NO UNA SECCIÓN DE OTRA ────────────────────
 *
 * Son dos preguntas distintas, y la regla de la casa dice que dos preguntas son
 * dos pantallas: *«¿cómo van mis cajas ahora?»* —quién tiene el turno abierto y
 * qué cerró cada uno— contra *«¿qué se cobró este día?»* —la plata, crédito por
 * crédito—. La primera vive en el portal del comercio
 * (`comercios-front`, `app/c/[code]/(portal)/cajas`); en ESTA consola no existe,
 * así que aquí no había nada a lo que colgarse: es ruta propia, `/console/cierre-de-caja`.
 *
 * Y el eje tampoco es el mismo. Cajas se ordena por TURNO; este cierre se ordena
 * por DÍA, y a propósito: hay ventas confirmadas que no cuelgan de ningún turno
 * (ver la cinta de la plantilla), y en una pantalla por turno esas ventas no
 * tendrían dónde aparecer.
 *
 * ── LO QUE NO HACE ────────────────────────────────────────────────────────────
 *
 * Se mira y se baja. Nada de esta pantalla abre ni cierra un turno, y no recalcula ningún
 * cuadre congelado — premisa de Juan del mismo día: *«todo lo que ajustes no puede
 * tocar la lógica que actualmente ya está»*. Son dos GET.
 *
 * ── LOS FILTROS VIVEN EN LA URL ───────────────────────────────────────────────
 *
 * Igual que en Solicitudes de crédito: la URL es la única fuente de verdad, el
 * formulario nunca dispara la búsqueda —sólo reescribe la URL—, y por eso un
 * enlace con el día y el cajero puestos se pega en un chat y abre lo mismo.
 */
@Component({
  selector: 'app-cierre-de-caja',
  templateUrl: './cierre-de-caja.component.html'
})
export class CierreDeCajaComponent implements OnInit, OnDestroy {

  private subQueryParams?: Subscription;
  private timerCedula: any;

  filtros: FiltrosCierre = {};

  /** Lo que hay tecleado en la caja de la cédula. Se vuelca a `filtros` con retardo. */
  cedulaTexto = '';

  /**
   * Tienda y caja se piden de a poco: un administrador mira el día completo y
   * sólo baja a una caja cuando algo no cuadra. Quedan detrás de este interruptor
   * para que el encabezado tenga DOS controles y no cuatro — *«mira esos filtros,
   * por Dios, tres mil filtros»*.
   *
   * ⚠️ Se abre solo cuando la URL trae alguno puesto: un filtro aplicado que no se
   * ve es peor que cuatro cajitas — la tabla sale recortada y no hay manera de
   * saber por qué.
   */
  masFiltros = false;

  resultado: any = null;
  cargando = true;
  bajando = false;

  error = '';
  errorReintentable = true;

  /**
   * 🔴 EL FALLO DE LA DESCARGA VA APARTE DEL DE LA LECTURA, y no es cosmético: si
   * los dos escribieran en `error`, un archivo que no se pudo bajar BORRARÍA de la
   * pantalla la tabla que ya estaba cargada y correcta — el operador perdería lo
   * que tenía delante por un problema que no es de esos datos.
   */
  errorAlBajar = '';

  /**
   * 🔴 El fallo que esta pantalla va a dar HOY, y merece su propio renglón en vez
   * de caer en el «intenta de nuevo» genérico: el endpoint saca el comercio del
   * usuario del token (`cierreDetallado`, primera línea) y un usuario de esta
   * consola nunca tiene comercio — así es como el núcleo reconoce a la gente de
   * CrediTotal (`casos/listado-solicitudes.js:esDeCrediTotal`). Ver el comentario
   * del servicio: falta el gemelo del lado admin.
   */
  faltaElComercio = false;

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _cierre: CierreCajaService
  ) { }

  ngOnInit(): void {
    this.subQueryParams = this.ruta.queryParams.subscribe((qp: Params) => {
      this.filtros = this.leerFiltrosDeUrl(qp);
      this.cedulaTexto = this.filtros.cedula || '';
      this.masFiltros = this.masFiltros || !!(this.filtros.tienda || this.filtros.caja);

      // Sin día en la URL, el enlace significaría «hoy» y mañana mostraría otra
      // cosa. Se fija el día que corre en Venezuela y se reescribe la dirección
      // SIN empujar historial: no es una decisión del operador, es el valor por
      // omisión hecho explícito.
      if (!this.filtros.fecha) {
        this.filtros.fecha = this.hoyEnVenezuela();
        this.router.navigate([], {
          relativeTo: this.ruta,
          queryParams: { ...qp, fecha: this.filtros.fecha },
          replaceUrl: true,
        });
        return;
      }
      this.cargar();
    });
  }

  ngOnDestroy(): void {
    this.subQueryParams?.unsubscribe();
    clearTimeout(this.timerCedula);
  }

  // ── URL ⇄ filtros ──────────────────────────────────────────────────────────

  /**
   * 🔴 EL COMERCIO SALE DE LA DIRECCIÓN, NO DEL SELECTOR — 2026-09-21.
   *
   * Esta pantalla pasó a ser una pestaña de la ficha
   * (`console/merchants/:code/cierre-de-caja`), así que el comercio ya viene
   * elegido en la ruta. Antes lo ponía sólo el interceptor desde el selector de
   * la cabecera, y con «Todos los comercios» la pantalla quedaba inerte — que
   * es justo lo que hacía que una entrada de menú de primer nivel no sirviera
   * para nada hasta elegir un comercio.
   *
   * ⚠️ Se lee con `paramsInheritanceStrategy: 'always'` (puesto en
   * `app-routing.module.ts`): sin eso, una hija de un padre CON componente no
   * hereda el `:code` y llega `null`.
   */
  private get comercioDeLaRuta(): string {
    return this.ruta.snapshot.paramMap.get('code') || '';
  }

  private leerFiltrosDeUrl = (qp: Params): FiltrosCierre => ({
    fecha: qp['fecha'] || '',
    cedula: qp['cedula'] || '',
    tienda: qp['tienda'] || '',
    caja: qp['caja'] || '',
    merchant_code: this.comercioDeLaRuta,
  });

  private actualizarUrl = (): void => {
    const qp: any = {};
    const f = this.filtros;
    if (f.fecha) qp.fecha = f.fecha;
    if (f.cedula) qp.cedula = f.cedula;
    if (f.tienda) qp.tienda = f.tienda;
    if (f.caja) qp.caja = f.caja;
    // `merchant_code` no viaja como consulta: ya está en el camino (`:code`).
    // Ponerlo en los dos lados daría dos verdades que se pueden contradecir.
    this.router.navigate([], { relativeTo: this.ruta, queryParams: qp });
  };

  /** La fecha y los dos desplegables cambian de una sola vez: no hace falta esperar. */
  onFiltroInmediato = (): void => this.actualizarUrl();

  /** La cédula se teclea dígito a dígito: se espera a que la mano pare. */
  onCedulaCambio = (): void => {
    clearTimeout(this.timerCedula);
    this.timerCedula = setTimeout(() => {
      this.filtros.cedula = (this.cedulaTexto || '').trim();
      this.actualizarUrl();
    }, 450);
  };

  onCedulaEnter = (): void => {
    clearTimeout(this.timerCedula);
    this.filtros.cedula = (this.cedulaTexto || '').trim();
    this.actualizarUrl();
  };

  /** El día NO se limpia: sin día no hay cierre, y el servidor caería en «hoy». */
  get hayFiltros(): boolean {
    const f = this.filtros;
    return !!(f.cedula || f.tienda || f.caja);
  }

  quitarFiltros = (): void => {
    this.cedulaTexto = '';
    this.filtros = { fecha: this.filtros.fecha };
    this.actualizarUrl();
  };

  /**
   * El día del negocio en Venezuela (UTC−4), que es el mismo que calcula el
   * servidor (`casos/cierre-de-caja.js:diaDelNegocio`). No se usa la fecha local
   * del navegador: una consola abierta en otro huso pediría otro día sin que
   * nadie se entere.
   */
  private hoyEnVenezuela = (): string =>
    new Date(Date.now() - 4 * 3600 * 1000).toISOString().slice(0, 10);

  // ── traer el cierre ────────────────────────────────────────────────────────

  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.error = '';
    this.errorReintentable = true;
    this.faltaElComercio = false;
    // Al cambiar de día, el fallo de la descarga anterior deja de aplicar: si se
    // quedara en pantalla, se leería como si el archivo de ESTE día fuera el que
    // falló.
    this.errorAlBajar = '';
    try {
      this.resultado = await this._cierre.getCierreDetallado(this.filtros);
    } catch (e: any) {
      this.resultado = null;
      // El servidor manda dos formas de error y ninguna es `e.error.msg` a secas:
      // la del módulo, `{error:{code,message,retryable}}` (`dominio/codigos.js`), y
      // la del middleware de JWT, `{msg}` sin envolver.
      const cuerpo = e?.error?.error;
      this.error = cuerpo?.message || e?.error?.msg
        || 'No se pudo leer el cierre. Intenta de nuevo en un momento.';
      this.errorReintentable = cuerpo?.retryable !== false;
      // 🔴 `merchant_code`, no `usuario`. El servidor dice qué le falta en
      // `error.falta`, y para esta pantalla lo que falta es el COMERCIO — lo
      // verifiqué en pantalla el 2026-09-20: con «Todos los comercios» puesto,
      // salía el aviso rojo de «no se pudo leer» en vez del cartel que manda a
      // elegir uno. `usuario` es el caso de un token sin usuario, que ya lo
      // ataja el guardia de la ruta.
      this.faltaElComercio = cuerpo?.code === 'DATOS_INCOMPLETOS'
                          && (cuerpo?.falta === 'merchant_code' || cuerpo?.falta === 'usuario');
    } finally {
      this.cargando = false;
    }
  };

  // ── lo que muestra la pantalla ─────────────────────────────────────────────

  get creditos(): any[] { return this.resultado?.creditos || []; }
  get total(): any { return this.resultado?.total || { creditos: 0, inicial_usd: 0, financiado_usd: 0 }; }

  /**
   * 🔴 SE MUESTRA TAL CUAL LO MANDA EL SERVIDOR, no copiado aquí: dice que el
   * cierre trae SÓLO las órdenes confirmadas. Sin ese renglón alguien cuadra
   * contra este número creyendo que están todas las solicitudes del día, y no le
   * va a dar. Si el alcance del endpoint cambia, la pantalla cambia con él.
   */
  get alcance(): string { return this.resultado?.alcance || ''; }

  /** El día que el servidor entendió — no el que se tecleó. Cuando hay rango, viene `null`. */
  get dia(): string { return this.resultado?.dia || this.filtros.fecha || ''; }

  /**
   * 🔴 EL RÓTULO DEL TOTAL CAMBIA CUANDO HAY UN FILTRO PUESTO, y no es un detalle
   * de redacción: con la cédula de un cajero puesta, un pie que dijera «Total del
   * día» estaría afirmando que ese es el día completo. Alguien cuadra contra ese
   * número, le falta la mitad de las ventas, y el error no tiene cómo verse.
   * Lo mismo vale para el botón que baja el archivo, que trae exactamente esto.
   */
  get esElDiaCompleto(): boolean { return !this.hayFiltros; }

  formatoMonto = (n: any): string =>
    (n === null || n === undefined) ? '—' : `$${Number(n).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  /**
   * El punto de venta de una fila, en un renglón: tienda, caja y cajero.
   * Se arma aquí y no en la plantilla para que el `—` de «ninguno de los tres
   * llegó» sea uno solo, y no tres guiones seguidos.
   */
  puntoDeVenta = (c: any): string => {
    const partes: string[] = [];
    if (c?.tienda !== null && c?.tienda !== undefined && c?.tienda !== '') partes.push(`Tienda ${c.tienda}`);
    if (c?.caja !== null && c?.caja !== undefined && c?.caja !== '') partes.push(`caja ${c.caja}`);
    return partes.join(' · ');
  };

  // ── bajar el archivo ───────────────────────────────────────────────────────

  /**
   * 🔴 Baja EXACTAMENTE lo que se está viendo: el mismo día y los mismos filtros.
   * Un botón que baja «el día entero» mientras la pantalla muestra una caja sola
   * entrega un archivo que no cuadra contra lo que el operador tiene delante.
   */
  bajar = async (): Promise<void> => {
    if (this.bajando) return;
    this.bajando = true;
    this.errorAlBajar = '';
    try {
      await this._cierre.bajarCsv(this.filtros);
    } catch (e: any) {
      const detalle = await this._cierre.mensajeDeFalloAlBajar(e);
      this.errorAlBajar = detalle
        ? `No se pudo bajar el archivo: ${detalle}`
        : 'No se pudo bajar el archivo. Lo que está en pantalla sigue siendo lo del día; prueba de nuevo en un momento.';
    } finally {
      this.bajando = false;
    }
  };
}
